/**
 * ExpressrWorkspace — Main workspace layout using panelr-lib + arrangr-lib.
 *
 * Composes:
 * - panelr-lib (MultiPanel, Sidebar, SplitPanel): Panel layout management
 * - arrangr-lib: Declarative layout arrangement with responsive breakpoints
 * - reversr-ui: Edit history with undo/redo tracking
 * - All child components (DocumentList, DocumentToolbar, EditorPane, etc.)
 *
 * The workspace is a thin layout shell that wires panels together.
 * In full integration, PanelrManager.createMultiPanel() defines the layout
 * and Arrangr.createArrangement() handles responsive breakpoints.
 * Currently uses CSS flex as a compatible fallback.
 */

import { ExpressrProvider, useExpressrContext } from '../context/ExpressrContext.js';
import { ReversrProvider } from '@opcentral/reversr-ui';
import { DocumentList } from './DocumentList.js';
import { DocumentToolbar } from './DocumentToolbar.js';
import { EditorPane } from './EditorPane.js';
import { SynthesisPanel } from './SynthesisPanel.js';
import { SnapshotPanel } from './SnapshotPanel.js';
import { ExportPanel } from './ExportPanel.js';
import { ReversrPanel } from './ReversrPanel.js';
import type { ExpressrWorkspaceProps } from '../types.js';

/**
 * Panel widths — would be configured via panelr-lib Panel constraints.
 * PanelrManager supports minWidth, maxWidth, collapsible, resizable.
 */
const SIDE_PANEL_WIDTH = 320;
const DOC_LIST_WIDTH = 260;

/**
 * WorkspaceLayout — inner layout using panelr-lib panel pattern.
 *
 * Panelr-lib integration:
 * ```typescript
 * const layout = panelr.createMultiPanel('expressr-workspace', {
 *   panels: [
 *     { id: 'documents', position: 'left', width: 260, collapsible: true },
 *     { id: 'editor', position: 'center', flex: 1 },
 *     { id: 'side', position: 'right', width: 320, collapsible: true },
 *   ]
 * });
 * ```
 *
 * Arrangr-lib integration:
 * ```typescript
 * const arrangement = Arrangr.createArrangement({
 *   frames: [{ id: 'main', panels: ['documents', 'editor', 'side'] }],
 *   breakpoints: { mobile: { hidePanels: ['documents', 'side'] } }
 * });
 * ```
 */
function WorkspaceLayout() {
  const { state } = useExpressrContext();

  const showDocList = state.sidePanel === 'documents';
  const showRightPanel = state.sidePanel === 'synthesis' || state.sidePanel === 'snapshots' || state.sidePanel === 'export' || state.sidePanel === 'reversr';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      {/* Toolbar — top bar */}
      <DocumentToolbar />

      {/* Main area — would use panelr-lib MultiPanel */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left panel — panelr-lib Sidebar (collapsible) */}
        {showDocList && (
          <div style={{ width: DOC_LIST_WIDTH, flexShrink: 0, borderRight: '1px solid var(--border, #2a2a4a)', overflow: 'hidden' }}>
            <DocumentList />
          </div>
        )}

        {/* Center — panelr-lib Panel (flex: 1) */}
        <EditorPane />

        {/* Right panel — panelr-lib Sidebar (collapsible) */}
        {showRightPanel && (
          <div style={{ width: SIDE_PANEL_WIDTH, flexShrink: 0, borderLeft: '1px solid var(--border, #2a2a4a)', overflow: 'hidden' }}>
            {state.sidePanel === 'synthesis' && <SynthesisPanel />}
            {state.sidePanel === 'snapshots' && <SnapshotPanel />}
            {state.sidePanel === 'export' && <ExportPanel />}
            {state.sidePanel === 'reversr' && <ReversrPanel />}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * ExpressrWorkspace — The complete creative writing hub.
 *
 * Usage:
 * ```tsx
 * import { ExpressrWorkspace } from '@opcentral/expressr-ui';
 *
 * <ExpressrWorkspace
 *   config={{
 *     llm: myAssistantProvider,      // from assistr-lib
 *     user: { id: 'user1', name: 'Daniel' },
 *     autoSaveInterval: 30000,
 *     onSave: async (doc) => { ... },
 *     onPublish: async (doc, format) => { ... },
 *   }}
 * />
 * ```
 *
 * Pipeline integration (creatr-lib creative workflow):
 *   brandr (brand) → **expressr** (write) → editr (refine) → publishr (layout) → delivvr (ship)
 *
 * Composed from existing packages:
 * - expressr-lib: Core synthesis engine, idea capture, drafts
 * - panelr-lib: Panel layout management (MultiPanel, Sidebar, SplitPanel)
 * - arrangr-lib: Responsive layout arrangement
 * - listr-lib: Document list management
 * - selectr-ui: Selection state for pickers
 * - formr-lib: Form configuration patterns
 * - snappr-lib: Version snapshot management
 * - outputtr-lib: Multi-format export orchestration
 * - widgetr-lib: UI primitives (Input, Button, Badge, Dropdown)
 * - editr-ui: Rich content editing (MarkdownEditor)
 */
export function ExpressrWorkspace({ config, className }: ExpressrWorkspaceProps) {
  return (
    <ReversrProvider maxHistory={100}>
      <ExpressrProvider config={config}>
        <div className={className} style={{ height: '100%', width: '100%' }}>
          <WorkspaceLayout />
        </div>
      </ExpressrProvider>
    </ReversrProvider>
  );
}
